import { useEffect, useState } from 'react';
import { cn } from '@/shared/utils/styling.ts';
import {
  IconTextItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  useFormField,
} from '@/shared/components/index.ts';
import * as SelectPrimitive from '@radix-ui/react-select';
import type { FieldError } from 'react-hook-form';

interface Option {
  value: string;
  label: string;
  icon?: string;
  code?: string;
}

type SelectOption = Option | string | number | unknown;

type RenderOptionFunc = (option: unknown) => React.ReactNode;

type SelectVariant = 'default' | 'simple';

type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> & {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
  options: SelectOption[];
  error?: FieldError;
  floatingLabel?: string;
  valueKey?: string;
  labelKey?: string;
  iconKey?: string;
  selectedKey?: string;
  variant?: SelectVariant;
  optionVariant?: 'default' | 'label' | 'inverted';
  classNameViewport?: string;
  selectContentClassname?: string;
  align?: SelectPrimitive.SelectContentProps['align'];
  withChevron?: boolean;
  customOptionsRender?: RenderOptionFunc;
  renderSelected?: RenderOptionFunc;
  onChange?: (value: string) => void;
  onClickCapture?: (event: React.MouseEvent<HTMLDivElement>) => void;
  sideOffset?: number;
};

const getValue = (option: SelectOption, valueKey?: string): string => {
  if (typeof option === 'string' || typeof option === 'number') {
    return option.toString();
  }

  if (typeof option !== 'object' || option === null) {
    return '';
  }

  if (valueKey) {
    const val = (option as Record<string, string | number>)[valueKey];

    if (typeof val === 'number') {
      return val.toString();
    }

    return val || '';
  }

  return (option as Option).value;
};

const getLabel = (option: SelectOption, labelKey?: string): string => {
  if (!option) {
    return '';
  }

  if (typeof option === 'string' || typeof option === 'number') {
    return option.toString();
  }

  if (typeof option !== 'object') {
    return '';
  }

  if (labelKey !== undefined) {
    return (option as Record<string, string>)[labelKey] || '';
  }

  return (option as Option).label || '';
};

function getIcon(option: SelectOption, iconKey?: string): string | undefined {
  if (typeof option !== 'object' || option === null) {
    return undefined;
  }

  if (iconKey !== undefined) {
    return (option as Record<string, string>)[iconKey];
  }

  return (option as Option).icon;
}

function getOptionValue(
  option: SelectOption,
  optionVariant?: string,
  valueKey?: string,
  labelKey?: string
): string {
  if (optionVariant === 'label' || optionVariant === 'inverted') {
    return getLabel(option, labelKey) || '';
  }

  return getValue(option, valueKey) || '';
}

function getOptionsLabel(
  option: SelectOption,
  optionVariant?: string,
  valueKey?: string,
  labelKey?: string
): string | undefined {
  if (optionVariant === 'label') {
    return undefined;
  }

  if (optionVariant === 'inverted') {
    return getValue(option, valueKey);
  }

  return getLabel(option, labelKey);
}

function renderOption(
  option: unknown,
  labelKey?: string,
  variant = 'default',
  iconKey?: string,
  valueKey?: string,
  optionVariant?: string,
  customOptionsRender?: RenderOptionFunc
) {
  if (customOptionsRender) {
    return customOptionsRender(option);
  }

  return (
    <SelectItem
      key={getValue(option, valueKey) || getLabel(option, valueKey)}
      value={getValue(option, valueKey)}
      showOptionCheckmark={false}
      className="px-6 py-3 h-auto rounded-lg min-h-[6rem] cursor-pointer">
      <div className="flex items-center justify-between w-full">
        {variant === 'simple' ? (
          <IconTextItem title={getLabel(option, labelKey)} />
        ) : (
          <IconTextItem
            icon={getIcon(option, iconKey)}
            title={getOptionValue(option, optionVariant, valueKey, labelKey)}
            name={getOptionsLabel(option, optionVariant, valueKey, labelKey)}
            variant="default"
          />
        )}
      </div>
    </SelectItem>
  );
}

function renderSelectedValue(
  option: string | unknown,
  labelKey?: string,
  iconKey?: string,
  valueKey?: string,
  selectedKey?: string,
  renderSelected?: RenderOptionFunc,
  disabled?: boolean
) {
  if (!option) {
    return <></>;
  }

  if (renderSelected) {
    return renderSelected(option);
  }

  return (
    <IconTextItem
      icon={getIcon(option, iconKey)}
      title={
        selectedKey
          ? getLabel(option, selectedKey)
          : getLabel(option, labelKey) || getValue(option, valueKey)
      }
      variant="compact"
      className={cn(disabled ? 'text-muted-foreground' : '')}
    />
  );
}

export function SelectInput({
  value = '',
  onValueChange,
  disabled = false,
  options,
  placeholder,
  floatingLabel,
  valueKey,
  labelKey,
  iconKey,
  selectedKey,
  variant,
  optionVariant,
  error,
  onChange: fieldOnChange,
  classNameViewport,
  align = 'start',
  withChevron = true,
  selectContentClassname,
  customOptionsRender,
  renderSelected,
  triggerClassName,
  sideOffset,
  ...props
}: SelectProps) {
  const [selectedOption, setSelectedOption] = useState<unknown>('');
  const [currentValue, setCurrentValue] = useState(value || '');

  let formFieldError: FieldError | undefined;
  try {
    const formField = useFormField();
    formFieldError = formField.error;
  } catch {
    // Not within a form field context
  }

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const hasError = !!(error || formFieldError);

  useEffect(() => {
    setSelectedOption(
      (options || []).find(
        option => getValue(option, valueKey) === currentValue
      )
    );
  }, [currentValue, options, valueKey]);

  const handleOptionSelect = (value: string) => {
    setCurrentValue(value);
    if (onValueChange) {
      onValueChange(value);
    } else if (fieldOnChange) {
      fieldOnChange(value);
    }
  };

  return (
    <Select value={currentValue} onValueChange={handleOptionSelect} {...props}>
      <SelectTrigger
        withChevron={withChevron}
        disabled={disabled}
        size="md"
        className={cn(
          'headline4 w-full h-[6rem] rounded-[1.6rem] px-6 bg-muted aria-invalid:bg-light-error dark:aria-invalid:bg-light-error',
          !disabled && 'cursor-pointer',
          triggerClassName
        )}
        floatingLabel={floatingLabel}
        placeholder={placeholder}
        value={currentValue}
        aria-invalid={hasError}>
        {renderSelectedValue(
          selectedOption,
          labelKey,
          iconKey,
          valueKey,
          selectedKey,
          renderSelected,
          disabled
        )}
      </SelectTrigger>

      <SelectContent
        className={cn(
          'rounded-[1.6rem] border border-border py-[1.2rem]',
          selectContentClassname
        )}
        align={align}
        classNameViewport={classNameViewport}
        position="popper"
        side="bottom"
        sideOffset={sideOffset}
        avoidCollisions={false}>
        {options.map(option =>
          renderOption(
            option,
            labelKey,
            variant,
            iconKey,
            valueKey,
            optionVariant,
            customOptionsRender
          )
        )}
      </SelectContent>
    </Select>
  );
}
